import { Immutable } from "immer";
import React from "react";
import {
    CAPTION_LIGHT_CLASS_NAME,
    CAPTION_MEDIUM_CLASS_NAME,
} from "tsi-common-react/src/utils/typography";
import { t } from "ttag";

import type { ProductBundlesBlock as ProductBundlesBlockValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    EditableUserLocation,
    ShippingArrivalDate,
    useShippingLocalization,
} from "../ShippingLocalization";

import styles from "./index.module.scss";

type IProps = Immutable<{ value: ProductBundlesBlockValue }>;

const wrapRichText = (value: string | null): string => {
    if (!value) {
        return "";
    }
    if (value.includes('class="rich-text"')) {
        return value;
    }
    return `<div class="rich-text">${value}</div>`;
};

const getImageAlt = (
    image: NonNullable<
        NonNullable<IProps["value"]["products"]>[number]["image"]
    >,
): string => {
    return image.title;
};

export const ProductBundlesBlock = (props: IProps): React.JSX.Element => {
    const localization = useShippingLocalization();
    return (
        <section className={styles.productBundlesBlock}>
            <div className={styles.productBundlesBlockContainer}>
                <h2 className={styles.productBundlesBlockHeader}>
                    {props.value.header}
                </h2>

                <h5 className={styles.productBundlesBlockSubheader}>
                    {t`DELIVERED BY UPS TO`}{" "}
                    <EditableUserLocation localization={localization} />
                    <ShippingArrivalDate
                        localization={localization}
                        prefix=" as soon as "
                    />
                </h5>

                <div className={styles.productBundlesBlockBody}>
                    <ul className={styles.productBundlesBlockProductItems}>
                        {(props.value.products ?? []).map((product, index) => (
                            <React.Fragment key={index}>
                                <li className={styles.productBundlesBlockItem}>
                                    {product.image ? (
                                        <img
                                            className="responsive-img"
                                            src={product.image.url}
                                            alt={getImageAlt(product.image)}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    ) : null}
                                    <div
                                        className={
                                            styles.productBundlesBlockItemCopy
                                        }
                                        dangerouslySetInnerHTML={{
                                            __html: product.copy ?? "",
                                        }}
                                    ></div>
                                    <div
                                        className={CAPTION_MEDIUM_CLASS_NAME}
                                        dangerouslySetInnerHTML={{
                                            __html: wrapRichText(product.price),
                                        }}
                                    ></div>
                                </li>
                                <li className={styles.productBundlesBlockItem}>
                                    <div
                                        className={
                                            styles.productBundlesBlockItemPlus
                                        }
                                    >
                                        +
                                    </div>
                                </li>
                            </React.Fragment>
                        ))}
                    </ul>
                    <div className={styles.productBundlesBlockContent}>
                        <div
                            className={styles.productBundlesBlockPricing}
                            dangerouslySetInnerHTML={{
                                __html: wrapRichText(props.value.price),
                            }}
                        ></div>
                        <div
                            className={concatClassNames([
                                styles.productBundlesBlockCopy,
                                CAPTION_LIGHT_CLASS_NAME,
                            ])}
                        >
                            {props.value.copy}
                        </div>
                        <a
                            className={concatClassNames([
                                styles.productBundlesBlockButton,
                                "button",
                            ])}
                            href={props.value.link?.page?.url}
                        >
                            {props.value.link?.link_title}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};
